import env from "@/env.config";
import useProfile from "@/hooks/useProfile";
import useSyncMutation from "@/hooks/useSyncMutation";
import { QUERY_KEYS } from "@/libraries/constant";
import { CASE_TABS_VALUE, MESSAGE_TAG } from "@/schemas";
import { useSyncStore } from "@/stores/sync";
import type {
	GetCasesResponse,
	GetGroupsResponse,
	GetTeamsResponse,
} from "@/types";
import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";
import OneSignal from "react-onesignal";

type NotificationWillDisplayParams = Parameters<
	typeof OneSignal.Notifications.addEventListener<"foregroundWillDisplay">
>;
type NotificationClickParams = Parameters<
	typeof OneSignal.Notifications.addEventListener<"click">
>;

interface NotificationAdditionalData {
	type: "single" | "case" | "group" | "removed" | "invitation";
	messageable_id: string | null;
	message?: Record<string, any>;
	case_id?: number | string;
	channel_name?: string;
	tags?: string;
}

const OneSignalProvider = (props: React.PropsWithChildren) => {
	const { children } = props;
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const isInitOnesignal = useRef(false);
	const { profile } = useProfile();

	const memberSyncChatLastUnix = useSyncStore((s) => s.memberSyncChatLastUnix);
	const groupSyncLastUnix = useSyncStore((s) => s.groupSyncLastUnix);
	const caseSyncLastUnix = useSyncStore((s) => s.caseSyncLastUnix);
	const inboxSyncLastUnix = useSyncStore((s) => s.inboxSyncLastUnix);
	const { memberSyncChat, groupSync, caseSync, inboxMessagesSync } =
		useSyncMutation();

	const onDisplayNotification = useCallback<NotificationWillDisplayParams[1]>(
		async (e) => {
			const { notification } = e;
			if (!notification?.additionalData) return;
			const additionalData =
				notification.additionalData as NotificationAdditionalData;
			const { type } = additionalData;
			const message = additionalData?.message;
			const channel_name = message?.channel_name;

			const pushTags = additionalData?.tags ?? message?.tags;
			const pushChannel = additionalData?.channel_name ?? channel_name;
			const pushCaseId =
				additionalData?.case_id ??
				String(pushChannel ?? "")
					.split(".")
					.pop();
			if (type === "case" && pushTags === "carechat" && pushCaseId) {
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.GET_CARE_CHAT, String(pushCaseId)],
				});
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.GET_CARE_CHAT_MESSAGES, String(pushCaseId)],
				});
			}
			if (type === "case" && pushTags === "askhemi" && pushChannel) {
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.GET_MESSAGES, { channel_name: pushChannel }],
					exact: false,
				});
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.GET_DOCUMENTS],
					exact: false,
				});
			}

			if (type) {
				switch (type) {
					case "single":
						memberSyncChat.mutate({
							last_requested_at: memberSyncChatLastUnix,
						});
						break;
					case "group":
						groupSync.mutate({
							last_requested_at: groupSyncLastUnix,
						});
						break;
					case "case":
						caseSync.mutate({
							last_requested_at: caseSyncLastUnix,
						});
						if (additionalData?.messageable_id) {
							queryClient.invalidateQueries({
								queryKey: [
									QUERY_KEYS.GET_MESSAGE,
									+additionalData.messageable_id,
								],
							});
						}
						break;
					case "invitation":
						inboxMessagesSync.mutate({
							last_requested_at: inboxSyncLastUnix,
						});
						break;
					case "removed": {
						if (!message) break;
						const { action_type } = message;
						if (action_type === "case") {
							queryClient.invalidateQueries({
								queryKey: [QUERY_KEYS.GET_CASE],
								exact: false,
								type: "active",
							});
						}
						if (action_type === "group") {
							queryClient.invalidateQueries({
								queryKey: [QUERY_KEYS.GET_GROUPS],
								exact: false,
								type: "active",
							});
						}
						if (action_type === "team") {
							queryClient.invalidateQueries({
								queryKey: [QUERY_KEYS.GET_TEAMS],
								exact: false,
								type: "active",
							});
						}
						break;
					}
				}
			}
			if (channel_name && !additionalData?.messageable_id) {
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.GET_MESSAGES, { channel_name }],
					type: "active",
					exact: false,
				});
			}
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_UNREADS],
			});
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_MEMBER_HOME],
			});
		},
		[
			queryClient,
			groupSyncLastUnix,
			caseSyncLastUnix,
			inboxSyncLastUnix,
			memberSyncChatLastUnix,
			memberSyncChat,
			groupSync,
			caseSync,
			inboxMessagesSync,
		],
	);

	const onClickNotification = useCallback<NotificationClickParams[1]>(
		(e) => {
			if (!e.notification) return;
			const { notification } = e;
			const { type, message } =
				notification.additionalData as NotificationAdditionalData;
			const clickData =
				notification.additionalData as NotificationAdditionalData;

			if (message?.channel_name?.includes("hemi")) {
				return navigate({
					to: "/dashboard/chat/single/hemi",
				});
			}

			if (type) {
				switch (type) {
					case "single": {
						const memberId = message?.user?.id;
						if (!memberId) {
							navigate({
								to: "/dashboard/chat/single",
							});
							break;
						}
						navigate({
							to: "/dashboard/chat/single/$id/$channelName",
							params: { id: +memberId, channelName: message?.channel_name },
						});
						break;
					}
					case "group": {
						const groupId = message?.group?.id;
						const tags = message?.tags;
						if (!groupId) {
							return navigate({
								to: "/dashboard/chat/group",
							});
						}
						return navigate({
							to: "/dashboard/chat/group/$id/$channelName",
							params: { id: +groupId, channelName: message?.channel_name },
							search: {
								...(tags === MESSAGE_TAG.AI
									? {
											tab: MESSAGE_TAG.AI,
										}
									: {}),
							},
						});
					}
					case "case": {
						const channel_name =
							clickData.channel_name ?? message?.channel_name;
						const tags = clickData.tags ?? message?.tags;
						const caseId = String(channel_name).split(".").pop();
						if (!caseId) {
							return navigate({
								to: "/dashboard/case/$status",
								params: { status: "active" },
							});
						}
						if (tags === "carechat" || tags === "askhemi") {
							return navigate({
								to: "/dashboard/case/view/$id",
								params: { id: +caseId },
								search:
									tags === "carechat"
										? { tab: CASE_TABS_VALUE.CARECHAT }
										: { tab: MESSAGE_TAG.FILES, subtab: MESSAGE_TAG.ASKHEMI },
							});
						}
						if (
							notification.additionalData &&
							"messageable_id" in notification.additionalData &&
							notification.additionalData.messageable_id
						) {
							return navigate({
								to: "/dashboard/case/message/$id",
								params: { id: notification.additionalData.messageable_id },
								search: { cid: +caseId },
							});
						}
						return navigate({
							to: "/dashboard/case/view/$id",
							params: { id: +caseId },
							search: {
								...(tags === MESSAGE_TAG.AI
									? {
											tab: MESSAGE_TAG.AI,
										}
									: {}),
							},
						});
					}
					case "invitation":
						return navigate({
							to: "/dashboard/inbox",
						});
				}
			}
		},
		[navigate],
	);

	const initializeOneSignal = useCallback(async () => {
		if (!profile) return;
		try {
			!env.isProd && OneSignal.Debug.setLogLevel("error");
			if (profile.id) {
				await OneSignal.login(profile.id.toString());
			}
			OneSignal.Notifications.setDefaultUrl(env.appUrl);
			if (!OneSignal.Notifications.permission) {
				await OneSignal.Notifications.requestPermission();
			}
			try {
				profile.email && OneSignal.User.addEmail(profile.email);
				profile.phone_number && OneSignal.User.addSms(profile.phone_number);
				profile.fullname && OneSignal.User.addTag("name", profile.fullname);
			} catch (metaErr) {
				console.warn("OneSignal profile metadata sync error:", metaErr);
			}
		} catch (error) {
			console.error("OneSignal initialization error:", error);
			isInitOnesignal.current = false;
		}
	}, [profile]);

	useEffect(() => {
		if (!isInitOnesignal.current && profile) {
			isInitOnesignal.current = true;
			initializeOneSignal();
		}
	}, [initializeOneSignal, profile]);

	useEffect(() => {
		OneSignal.Notifications.addEventListener(
			"foregroundWillDisplay",
			onDisplayNotification,
		);
		OneSignal.Notifications.addEventListener("click", onClickNotification);

		return () => {
			OneSignal.Notifications.removeEventListener(
				"foregroundWillDisplay",
				onDisplayNotification,
			);
			OneSignal.Notifications.removeEventListener("click", onClickNotification);
		};
	}, [onDisplayNotification, onClickNotification]);

	return children;
};

export default OneSignalProvider;
